"use client";
import Image from "next/image";
import logo from "@public/logo.svg";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { MediaStorage } from "@shared/utils";
import { Logger } from "@shared/utils/logger";
import { Shield, Key, Eye, EyeOff } from "lucide-react";
import { MeetingType } from "@shared/constants/constant";
import { toastNotify } from "@client/components/ui/toast";
import { useAppDispatch } from "@client/store/store.hooks";
import { LocalUserChoices } from "@livekit/components-react";
import { PreJoinView } from "@client/components/meet/PreJoinView";
import { createMeeting } from "@client/actions/meeting/meeting.actions";
import { encodePassphrase, randomString } from "@client/utils/client-utils";
import { Suspense, useState, useEffect, useCallback, ChangeEvent } from "react";
import { setMediaDefaults, setIsJoining } from "@client/store/slices/preJoin.slice";

/**
 * StartMeeting component - uses PreJoinView to create a seamless experience
 */
const StartMeeting = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const t = useTranslations();

  const [e2ee, setE2ee] = useState(false);
  const [sharedPassphrase, setSharedPassphrase] = useState(() => randomString(64));
  const [showPassphrase, setShowPassphrase] = useState(false);

  useEffect(() => {
    const prefs = MediaStorage.getPreferences();
    dispatch(
      setMediaDefaults({
        audioEnabled: prefs.micEnabled,
        videoEnabled: prefs.cameraEnabled
      })
    );
  }, [dispatch]);

  // Handle meeting creation
  const handleCreateMeeting = useCallback(
    async (choices: LocalUserChoices, _shouldPresent: boolean = false) => {
      dispatch(setIsJoining(true));

      try {
        const createMeetingResponse = await createMeeting({
          title: "Instant Meeting create for Web",
          description: "Instant Meeting created via Web Client",
          meetingType: MeetingType.INSTANT,
          isPublicEvent: false,
          isNotify: false,
          participantCount: 30
        });

        if (!createMeetingResponse.status) {
          const loginUrl = createMeetingResponse.loginUrl || "";
          const errorCode = createMeetingResponse.errorCode || "";
          const isAuthError = errorCode === "AUTH_REQUIRED" || errorCode.startsWith("AUTH_");
          const detail = createMeetingResponse.message?.trim();
          const debugHint = createMeetingResponse.debug?.hint;

          if (isAuthError && (detail || debugHint)) {
            Logger.warn("StartMeeting", "createMeeting auth error", {
              errorCode,
              debug: createMeetingResponse.debug
            });
          }

          toastNotify.info(
            isAuthError && detail
              ? debugHint
                ? `${detail} (${debugHint})`
                : detail
              : isAuthError
                ? t("toast.info.loginRequired", { url: loginUrl })
                : createMeetingResponse.message || t("toast.info.loginRequired", { url: loginUrl })
          );
          dispatch(setIsJoining(false));
          return;
        }

        const roomId = createMeetingResponse.data?.roomName;
        if (!roomId) {
          toastNotify.error(t("toast.error.roomIdMissing"));
          dispatch(setIsJoining(false));
          return;
        }

        // Store room as host in localStorage
        const hostRooms = JSON.parse(localStorage.getItem("hostRooms") || "[]");
        if (!hostRooms.includes(roomId)) {
          hostRooms.push(roomId);
          localStorage.setItem("hostRooms", JSON.stringify(hostRooms));
        }

        // Navigate to room
        const url = e2ee ? `/rooms/${roomId}#${encodePassphrase(sharedPassphrase)}` : `/rooms/${roomId}`;
        router.push(url);
      } catch (error) {
        Logger.error("StartMeeting", "Error:", error);
        dispatch(setIsJoining(false));
        toastNotify.error(t("toast.error.generic"));
      }
    },
    [e2ee, sharedPassphrase, router, dispatch, t]
  );

  const handleE2eeChange = useCallback((ev: ChangeEvent<HTMLInputElement>) => {
    setE2ee(ev.target.checked);
  }, []);

  const handlePassphraseChange = useCallback((ev: ChangeEvent<HTMLInputElement>) => {
    setSharedPassphrase(ev.target.value);
  }, []);

  return (
    <PreJoinView
      onSubmit={handleCreateMeeting}
      joinLabel={t("meeting.createMeeting")}
      title={t("meeting.createMeeting")}
      description={t("preJoin.createDescription")}
      hideNameInput={true}
      hidePresentButton={true}
    >
      {/* E2EE Controls injected into the form */}
      <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-3 mb-2">
        <label className="flex items-center gap-3 cursor-pointer">
          <div
            className={`p-1.5 rounded-lg transition-colors ${e2ee ? "bg-emerald-100 text-emerald-600" : "bg-gray-200 text-gray-500"}`}
          >
            <Shield className="w-4 h-4" aria-hidden="true" />
          </div>
          <div className="flex-1 select-none">
            <p className="text-sm font-medium text-gray-900">{t("settings.endToEndEncryption")}</p>
          </div>
          <div className={`w-10 h-5 rounded-full p-1 transition-colors ${e2ee ? "bg-(--primary)" : "bg-gray-300"}`}>
            <div
              className={`w-3 h-3 rounded-full bg-white shadow-sm transition-transform ${e2ee ? "translate-x-5" : "translate-x-0"}`}
            />
          </div>
          <input type="checkbox" checked={e2ee} onChange={handleE2eeChange} className="sr-only" />
        </label>

        {e2ee && (
          <div className="animate-in slide-in-from-top-1 fade-in duration-200 pt-1">
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Key
                  className="h-4 w-4 text-gray-400 group-focus-within:text-(--primary) transition-colors"
                  aria-hidden="true"
                />
              </div>
              <input
                type={showPassphrase ? "text" : "password"}
                value={sharedPassphrase}
                onChange={handlePassphraseChange}
                className="block w-full pl-9 pr-10 py-2.5 text-sm text-gray-900 border border-gray-200 rounded-xl bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-(--primary)/20 focus:border-(--primary) transition-all"
                placeholder={t("settings.enterPassphrase")}
                autoComplete="off"
                aria-label={t("settings.encryptionPassphrase")}
              />
              <button
                type="button"
                onClick={() => setShowPassphrase((prev) => !prev)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--primary) focus-visible:ring-offset-2 rounded"
                aria-label={showPassphrase ? t("settings.hidePassphrase") : t("settings.showPassphrase")}
              >
                {showPassphrase ? (
                  <EyeOff className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <Eye className="h-4 w-4" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </PreJoinView>
  );
};

/**
 * Home page component
 */
export default function Page() {
  return (
    <main className="flex flex-col min-h-screen bg-white">
      <header className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex items-center gap-2">
          <Image
            src={logo}
            alt="Freiburger Meet"
            width={140}
            height={28}
            style={{ height: "auto" }}
            className="sm:w-45"
            priority
          />
        </div>
      </header>

      <div className="flex-1 flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-350">
          <div className="w-full">
            <Suspense fallback={<div className="text-center text-gray-500">Loading...</div>}>
              <StartMeeting />
            </Suspense>
          </div>
        </div>
      </div>
    </main>
  );
}
